import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChefHat, Factory, TrendingUp, Heart, CheckCircle } from 'lucide-react';
import { heroData, whyChooseUs, partners } from '../mockData';

const Home = () => {
  const iconMap = {
    ChefHat: ChefHat,
    Factory: Factory,
    TrendingUp: TrendingUp,
    Heart: Heart
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center pt-20">
        <div className="absolute inset-0 z-0">
          <img 
            src={heroData.image}
            alt="Food Manufacturing"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-2xl">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              {heroData.title}
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-4 font-medium">
              {heroData.subtitle}
            </p>
            <p className="text-lg text-gray-300 mb-8">
              {heroData.description}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="group bg-emerald-600 text-white px-8 py-4 rounded-none font-semibold text-lg flex items-center gap-2 transition-all duration-300 hover:bg-emerald-700 hover:shadow-xl hover:-translate-y-1"
              >
                Partner With Us
                <ArrowRight className="group-hover:translate-x-1 transition-transform duration-300" size={20} />
              </Link>
              <Link
                to="/services"
                className="bg-white text-emerald-600 px-8 py-4 rounded-none font-semibold text-lg transition-all duration-300 hover:bg-gray-100 hover:shadow-xl hover:-translate-y-1"
              >
                Our Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Fresh Bites Group?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We combine traditional culinary expertise with modern manufacturing excellence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => {
              const Icon = iconMap[item.icon];
              return (
                <div 
                  key={index}
                  className="bg-white p-8 rounded-none shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-t-4 border-emerald-600"
                >
                  <div className="bg-emerald-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                    <Icon size={32} className="text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Core Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From recipe to retail - complete manufacturing solutions for food brands
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="relative overflow-hidden rounded-none shadow-lg group">
              <img 
                src="https://images.unsplash.com/photo-1577219492769-b63a779fac28?w=800&q=80"
                alt="Recipe Development"
                className="w-full h-80 object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-end">
                <div className="p-6 text-white">
                  <h3 className="text-2xl font-bold mb-2">Recipe Development</h3>
                  <p className="text-gray-200 mb-4">Expert refinement and optimization for commercial success</p>
                  <Link to="/services" className="text-emerald-400 font-medium flex items-center gap-2 hover:gap-3 transition-all duration-300">
                    Learn More <ArrowRight size={18} />
                  </Link>
                </div>
              </div>
            </div>

            <div className="relative overflow-hidden rounded-none shadow-lg group">
              <img 
                src="https://images.unsplash.com/photo-1588416820614-f8d6ac6cea56?w=800&q=80"
                alt="Manufacturing"
                className="w-full h-80 object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-end">
                <div className="p-6 text-white">
                  <h3 className="text-2xl font-bold mb-2">Product Scaling</h3>
                  <p className="text-gray-200 mb-4">Scale from small batches to commercial production</p>
                  <Link to="/services" className="text-emerald-400 font-medium flex items-center gap-2 hover:gap-3 transition-all duration-300">
                    Learn More <ArrowRight size={18} />
                  </Link>
                </div>
              </div>
            </div>

            <div className="relative overflow-hidden rounded-none shadow-lg group">
              <img 
                src="https://images.unsplash.com/photo-1621494548002-bfc916172ead?w=800&q=80"
                alt="Quality Control"
                className="w-full h-80 object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-end">
                <div className="p-6 text-white">
                  <h3 className="text-2xl font-bold mb-2">Quality Assurance</h3>
                  <p className="text-gray-200 mb-4">HACCP certified with rigorous quality standards</p>
                  <Link to="/services" className="text-emerald-400 font-medium flex items-center gap-2 hover:gap-3 transition-all duration-300">
                    Learn More <ArrowRight size={18} />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center">
            <Link
              to="/services"
              className="inline-block bg-emerald-600 text-white px-8 py-4 rounded-none font-semibold text-lg transition-all duration-300 hover:bg-emerald-700 hover:shadow-xl hover:-translate-y-1"
            >
              View All Services
            </Link>
          </div>
        </div>
      </section>

      {/* Trusted Partners */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Trusted By Leading Brands
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Partnering with innovative food brands across the UK
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {partners.map((partner, index) => (
              <div key={index} className="bg-white rounded-none shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <img 
                  src={partner.image}
                  alt={partner.name}
                  className="w-full h-64 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{partner.name}</h3>
                  <p className="text-emerald-600 font-medium mb-3">{partner.location}</p>
                  <p className="text-gray-600 mb-4">{partner.description}</p>
                  <div className="border-l-4 border-emerald-600 pl-4 bg-gray-50 p-4">
                    <p className="text-gray-700 italic">"{partner.testimonial}"</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/partners"
              className="inline-block bg-emerald-600 text-white px-8 py-4 rounded-none font-semibold text-lg transition-all duration-300 hover:bg-emerald-700 hover:shadow-xl hover:-translate-y-1"
            >
              View All Partners
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-emerald-600">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Scale Your Food Brand?
          </h2>
          <p className="text-xl text-emerald-100 mb-8 max-w-3xl mx-auto">
            Let's discuss how we can help manufacture your recipe and bring your vision to market
          </p>
          <Link
            to="/contact"
            className="inline-block bg-white text-emerald-600 px-10 py-5 rounded-none font-bold text-lg transition-all duration-300 hover:bg-gray-100 hover:shadow-2xl hover:-translate-y-1"
          >
            Get in Touch Today
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
